import { Box } from "../../box";
import { NodeType, useCollection, useScrollableCollection } from "../../collection";
import { forwardRef } from "react";
import { Keys, appendEventKey, cssModule, isEmptyArray, isNil, isNumber, mergeProps, useAutoFocusChild, useControllableState, useDisposables, useEventCallback, useFocusManager, useFocusScope, useId, useKeyedRovingFocus, useMergedRefs, useRefState } from "../../shared";
import { MenuContext } from "./MenuContext";
import { MenuItem } from "./MenuItem";
import { MenuSection } from "./MenuSection";
import { useResponsiveValue } from "../../styling";
import { jsx as _jsx } from "react/jsx-runtime";
export const ItemKeyProp = "data-o-ui-key";
const DefaultElement = "ul";

function useCollectionNodes(children, nodes) {
  const collectionNodes = useCollection(children);
  return nodes !== null && nodes !== void 0 ? nodes : collectionNodes;
}

export function InnerMenu({
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  as = DefaultElement,
  autoFocus,
  children,
  defaultFocusTarget,
  defaultSelectedKeys,
  fluid,
  forwardedRef,
  id,
  nodes: nodesProp,
  onSelectionChange,
  selectedKeys: selectedKeysProp,
  selectionMode = "none",
  validationState,
  ...rest
}) {
  var _selectedKeys$;

  const fluidValue = useResponsiveValue(fluid);
  const [selectedKeys, setSelectedKeys] = useControllableState(selectedKeysProp, defaultSelectedKeys, []);
  const [searchQueryRef, setSearchQuery] = useRefState("");
  const [focusScope, setFocusRef] = useFocusScope();
  const containerRef = useMergedRefs(setFocusRef, forwardedRef);
  const focusManager = useFocusManager(focusScope, {
    keyProp: ItemKeyProp
  });
  const handleSelectItem = useEventCallback((event, key) => {
    let newKeys;

    if (selectionMode === "multiple") {
      newKeys = selectedKeys.includes(key) ? selectedKeys.filter(x => x !== key) : [...selectedKeys, key];
    } else {
      newKeys = selectedKeys.includes(key) ? [] : [key];
    }

    if (selectionMode !== "none") {
      setSelectedKeys(newKeys);
    }

    if (!isNil(onSelectionChange)) {
      onSelectionChange(event, newKeys);
    }
  });
  const searchDisposables = useDisposables();
  const handleKeyDown = useEventCallback(event => {
    searchDisposables.dispose();

    switch (event.key) {
      case Keys.arrowDown:
        {
          event.preventDefault();
          focusManager.focusNext();
          break;
        }

      case Keys.arrowUp:
        {
          event.preventDefault();
          focusManager.focusPrevious();
          break;
        }

      case Keys.home:
        event.preventDefault();
        focusManager.focusFirst();
        break;

      case Keys.end:
        event.preventDefault();
        focusManager.focusLast();
        break;

      case Keys.enter:
      case Keys.space:
        event.preventDefault();
        handleSelectItem(event, document.activeElement.getAttribute(ItemKeyProp));
        break;
      // eslint-disable-next-line no-fallthrough

      default:
        if (event.key.length === 1) {
          event.preventDefault();
          const query = appendEventKey(searchQueryRef.current, event.key);
          setSearchQuery(query);
          focusManager.search(query); // Clear search query.

          searchDisposables.setTimeout(() => {
            setSearchQuery("");
          }, 350);
        }

    }
  });
  useKeyedRovingFocus(focusScope, selectedKeys[0], {
    keyProp: ItemKeyProp
  });
  useAutoFocusChild(focusManager, {
    delay: isNumber(autoFocus) ? autoFocus : undefined,
    isDisabled: !autoFocus,
    target: (_selectedKeys$ = selectedKeys[0]) !== null && _selectedKeys$ !== void 0 ? _selectedKeys$ : defaultFocusTarget
  });
  const scrollableProps = useScrollableCollection(containerRef, {
    disabled: selectionMode === "none",
    dividerSelector: ".o-ui-menu-divider",
    // A menu have a border-size of 1px
    itemSelector: ".o-ui-menu-item",
    maxHeight: 12 * 32,
    // 32px is the default menu item height.
    paddingHeight: 2 * 1,
    sectionSelector: ".o-ui-menu-section-title"
  });
  const nodes = useCollectionNodes(children, nodesProp);
  const rootId = useId(id, "o-ui-menu");

  const renderItem = ({
    content,
    elementType: As = MenuItem,
    index,
    key,
    props,
    ref,
    tooltip
  }) => /*#__PURE__*/_jsx(As, { ...mergeProps(props, {
      id: `${rootId}-item-${index + 1}`,
      item: {
        key,
        tooltip
      },
      key,
      ref
    }),
    children: content
  });

  const renderSection = ({
    key,
    index,
    elementType: As = MenuSection,
    ref,
    props,
    items: sectionItems
  }) => {
    if (isEmptyArray(sectionItems)) {
      return null;
    }

    return /*#__PURE__*/_jsx(As, { ...mergeProps(props, {
        id: `${rootId}-section-${index + 1}`,
        key,
        ref
      }),
      children: sectionItems.map(x => renderItem(x))
    });
  };

  const renderDivider = ({
    content,
    elementType: As,
    key,
    props,
    ref
  }) => /*#__PURE__*/_jsx(As, { ...mergeProps(props, {
      as: "li",
      className: "o-ui-menu-divider",
      key,
      ref
    }),
    children: content
  });

  return /*#__PURE__*/_jsx(Box, { ...mergeProps(rest, {
      "aria-invalid": validationState === "invalid" ? true : undefined,
      "aria-label": ariaLabel,
      "aria-labelledby": isNil(ariaLabel) ? ariaLabelledBy : undefined,
      "aria-orientation": "vertical",
      as,
      className: cssModule("o-ui-menu", fluidValue && "fluid", selectionMode !== "none" && "with-selection", validationState),
      id: rootId,
      onKeyDown: handleKeyDown,
      ref: containerRef,
      role: "menu"
    }, scrollableProps),
    children: /*#__PURE__*/_jsx(MenuContext.Provider, {
      value: {
        onSelect: handleSelectItem,
        selectedKeys,
        selectionMode
      },
      children: nodes.map(node => {
        switch (node.type) {
          case NodeType.item:
            return renderItem(node);

          case NodeType.section:
            return renderSection(node);

          case NodeType.divider:
            return renderDivider(node);

          default:
            return null;
        }
      })
    })
  });
}
InnerMenu.defaultElement = DefaultElement;
export const Menu = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/_jsx(InnerMenu, { ...props,
  forwardedRef: ref
}));